// src/services/ProgramService.ts

import { BaseService } from "@services/BaseService";
import { ProgramPayload } from "@shared/interfaces/ProgramFiles";

class ProgramService extends BaseService {
  createProgram(data: ProgramPayload) {
    return this.post("/programs", data);
  }

  getProgramById(programId: string | number) {
    return this.get(`/programs/${programId}`);
  }

  updateProgram(programId: string | number, data: Partial<ProgramPayload>) {
    return this.patch(`/programs/${programId}`, data);
  }

  deleteProgram(programId: string | number) {
    return this.delete(`/programs/${programId}`);
  }

  getAllPrograms(params?: { order?: string; offset?: number; limit?: number }) {
    return this.get("/programs", { params });
  }

  getMyPrograms(params?: { order?: string; offset?: number; limit?: number }) {
    return this.get("/my_programs", { params });
  }

  getProgramsLatestUpdates(programId: string | number) {
    return this.get(`/programs/${programId}/latest-updates`);
  }

  // --- Suggestion Endpoints ---
  getSuggestions(programId: string | number, programUpdateId?: number) {
    const params = programUpdateId
      ? { program_update_id: programUpdateId }
      : undefined;
    return this.get(`/programs/${programId}/suggestions`, { params });
  }

  applySuggestion(programId: string | number, suggestionId: number) {
    return this.post(
      `/programs/${programId}/suggestions/${suggestionId}/apply`
    );
  }

  ignoreSuggestion(programId: string | number, suggestionId: number) {
    return this.post(
      `/programs/${programId}/suggestions/${suggestionId}/ignore`
    );
  }

  applyAllSuggestions(programId: string | number, programUpdateId: number) {
    return this.post(
      `/programs/${programId}/suggestions/program-update/${programUpdateId}/apply-all`
    );
  }

  ignoreAllSuggestions(programId: string | number, programUpdateId: number) {
    return this.post(
      `/programs/${programId}/suggestions/program-update/${programUpdateId}/ignore-all`
    );
  }

  getProgramsDropdown() {
    return this.get("/programs/dropdown");
  }

  getProgramUpdatesByType(params: {
    programId: string | number;
    type: "goal" | "milestone";
    ids?: string[]; // Optional list of target IDs
    limit?: number; // Optional limit
  }) {
    const { programId, type, ids, limit } = params;
    const query: Record<string, string> = { type };

    if (ids && ids.length > 0) {
      query.ids = ids.join(",");
    }

    if (limit !== undefined) {
      query.limit = String(limit);
    }

    return this.get(`/programs/${programId}/updates`, { params: query });
  }
}

export const programService = new ProgramService();
